<template>
  <BaseVeeForm :action="handleFormSubmit">
    <template #default="formProps">
      <VModal ref="dialog" :disable-backdrop-click="formProps.isPending" @close="$emit('close')">
        <template #default="modalProps">
          <div class="rounded shadow-xs w-full max-w-3xl mx-auto">
            <div class="rounded shadow-lg bg-white">
              <div class="px-4 sm:px-6 py-5 border-b">
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <h1 class="text-sm">Select permissions</h1>
                  </div>

                  <div class="flex-none">
                    <button
                      type="button"
                      class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      @click="modalProps.close"
                    >
                      <span class="sr-only">Close</span>
                      <svg
                        class="inline-flex h-6 w-6"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div class="p-4 sm:p-6 space-y-8">
                <BaseFormTextFieldWithValidator
                  v-model="formData.name"
                  v-bind="roleNameFieldProps"
                />
                <div class="space-y-2">
                  <BaseFormLabel>Permissions</BaseFormLabel>
                  <table class="border">
                    <thead class="bg-gray-50 border-b">
                      <tr>
                        <th
                          class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Feature
                        </th>
                        <th
                          class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Permissions
                        </th>
                        <th
                          class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          <div class="flex gap-2 items-center">
                            <BaseButton type="button" small primary outline @click="selectAll()"
                              >All</BaseButton
                            >
                            <BaseButton type="button" small primary outline @click="deselectAll()"
                              >None</BaseButton
                            >
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        class="even:bg-white odd:bg-gray-50"
                        v-for="item in permissionsByFeature"
                        :key="item.handle"
                      >
                        <td class="px-4 py-4 text-sm font-medium align-top">
                          {{ item.display_name }}
                        </td>
                        <td class="px-4 py-4 align-top">
                          <BaseChipsBasic
                            v-model="formData.permissions"
                            multiple
                            :options="item.permissions"
                          />
                        </td>
                        <td class="px-4 py-4 align-top">
                          <div class="flex gap-2 items-center">
                            <BaseButton
                              type="button"
                              small
                              primary
                              outline
                              @click="selectAll(item.handle)"
                              >All</BaseButton
                            >
                            <BaseButton
                              type="button"
                              small
                              primary
                              outline
                              @click="deselectAll(item.handle)"
                              >None</BaseButton
                            >
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="flex gap-3 justify-end">
                  <BaseButton primary> Save </BaseButton>
                  <BaseButton type="button" ghost @click="modalProps.close()"> Cancel </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </template>
      </VModal>
    </template>
  </BaseVeeForm>
</template>

<script>
import { uniq } from 'lodash-es';
import { accountMembershipProductClient, rolesClient } from '@/api/clients';

export default {
  props: {
    roleId: {
      type: Number,
      required: true,
    },
    permissionOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      role: [],
      membershipProduct: [],
      formData: {
        name: '',
        permissions: [],
      },
    };
  },
  computed: {
    roleNameFieldProps() {
      return {
        validator: {
          name: 'name',
          vid: 'name',
          rules: { required: true },
        },
        field: {
          id: 'name',
          label: 'Role name',
        },
      };
    },
    permissionsByFeature() {
      return (this.membershipProduct?.features || []).map((feature) => {
        const permissionIds = (this.membershipProduct?.feature_permission_options || [])
          .filter((featurePermissionOption) => featurePermissionOption.feature_id === feature.id)
          .map((featurePermissionOption) => featurePermissionOption.permission_id);

        const permissions = (this.membershipProduct?.permissions || [])
          .filter(
            (permission) =>
              permissionIds.findIndex((permissionId) => permissionId === permission.id) > -1
          )
          .map((permission) => ({
            value: permission.id,
            label: permission.name,
          }));

        return {
          display_name: feature.display_name,
          handle: feature.handle,
          permissions,
        };
      });
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      const roleResponse = await rolesClient.read(this.roleId, { include: 'permissions' });
      this.role = roleResponse.data;
      this.formData.name = this.role?.name || '';
      this.formData.permissions = (this.role?.permissions || []).map((permission) => permission.id);

      const accountMembershipProductResponse = await accountMembershipProductClient.read(
        this.role.account_id,
        {
          include: 'features,featurePermissionOptions,permissions',
        }
      );

      this.membershipProduct = accountMembershipProductResponse.data;
    },
    selectAll(handle = null) {
      if (!handle) {
        this.formData.permissions = (this.membershipProduct.permissions || []).map(
          (permission) => permission.id
        );
        return;
      }

      const item = this.permissionsByFeature.find((i) => i.handle === handle);
      const itemPermissionIds = (item?.permissions || []).map((permission) => permission.value);

      this.formData.permissions = uniq([...this.formData.permissions, ...itemPermissionIds]);
      //
    },
    deselectAll(handle = null) {
      if (!handle) {
        this.formData.permissions = [];
        return;
      }

      const item = this.permissionsByFeature.find((i) => i.handle === handle);
      const itemPermissionIds = (item?.permissions || []).map((permission) => permission.value);

      this.formData.permissions = this.formData.permissions.filter(
        (permissionId) => itemPermissionIds.indexOf(permissionId) === -1
      );
    },
    handleFormSubmit() {
      return rolesClient.update(this.role.id, this.formData).then(() => {
        this.$emit('updated');
        this.$refs.dialog?.close();
      });
    },
  },
};
</script>
